import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from "@angular/router";
import { Observable, firstValueFrom, from } from "rxjs";
import { take } from "rxjs/operators";

import { AuthService } from "./auth.service";
import { PermissionKey, RolePermissionsService } from "./role-permissions.service";
import { TenantFeatureService } from "../tenant/tenant-feature.service";

@Injectable({ providedIn: "root" })
export class AuthGuardPermission implements CanActivate {
  constructor(
    private authService: AuthService,
    private rolePermissions: RolePermissionsService,
    private tenantFeatures: TenantFeatureService,
    private router: Router
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    return from(this.guard(route.data?.["permission"] as PermissionKey | undefined));
  }

  private async guard(permission: PermissionKey | undefined): Promise<boolean | UrlTree> {
    const userInfo = await firstValueFrom(this.authService.getAuthStatusListener().pipe(take(1)));
    if (!userInfo.isAuthenticated || !permission) {
      return this.router.createUrlTree(["/auth/unauthorized"]);
    }
    if (!this.tenantFeatures.isPermissionVisible(permission)) {
      return this.router.createUrlTree(["/"]);
    }

    try {
      const profile = await firstValueFrom(this.authService.getProfile());
      if (!this.authService.isProfileComplete(profile)) {
        return this.router.createUrlTree(["/auth/complete-profile"]);
      }

      const settings = await firstValueFrom(this.rolePermissions.getRolePermissions());
      const allowed = this.rolePermissions.hasPermission(
        this.authService.getUserRole(),
        permission,
        settings.rolePermissions
      );

      return allowed ? true : this.router.createUrlTree(["/auth/unauthorized"]);
    } catch {
      return this.router.createUrlTree(["/auth/unauthorized"]);
    }
  }
}
